import { useEffect, useState } from 'react';
import { lookupAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button, Modal, Input, Select, Loading, Alert } from './ui';

// Categories that can be managed here
const CATEGORIES = [
  { key:'body_type',     label:'Type of Body',          note:'Vehicle form — CR details' },
  { key:'vehicle_class', label:'Class of Vehicle',      note:'Vehicle form — CR details' },
  { key:'reg_status',    label:'Status when Registered',note:'Vehicle form — CR details' },
  { key:'tax_class',     label:'Tax Class',             note:'Vehicle form — CR details' },
  { key:'fuel_type',     label:'Fuel Type',             note:'Vehicle & fuel forms' },
  { key:'license_type',  label:'Driver Licence Type',   note:'Driver form' },
  { key:'project',       label:'Projects',              note:'Assignment form' },
  { key:'location',      label:'Locations',             note:'Assignment form' },
];

export default function LookupLists() {
  const { can } = useAuth();
  const [rows,    setRows]    = useState([]);
  const [cat,     setCat]     = useState('body_type');
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [modal,   setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({ category:'body_type', value:'', sort_order:0, status:'active' });
  const [saving,  setSaving]  = useState(false);

  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 3000); };

  const load = () => {
    setLoading(true);
    lookupAPI.getAll({ all: 1 })
      .then(r => setRows(r.data.rows || []))
      .catch(() => setError('Failed to load lists'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openAdd  = () => { setEditing(null); setForm({ category: cat, value:'', sort_order: (visible.length + 1) * 1, status:'active' }); setModal(true); };
  const openEdit = (r) => { setEditing(r); setForm({ category: r.category, value: r.value, sort_order: r.sort_order, status: r.status }); setModal(true); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) await lookupAPI.update(editing.id, form);
      else         await lookupAPI.create(form);
      flash(editing ? 'Item updated' : 'Item added');
      setModal(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const toggle = async (r) => {
    try { await lookupAPI.update(r.id, { status: r.status === 'active' ? 'inactive' : 'active' }); load(); }
    catch { setError('Failed to update'); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove "${r.value}"? Existing records keep their saved value.`)) return;
    try { await lookupAPI.remove(r.id); flash('Item removed'); load(); }
    catch (err) { setError(err.response?.data?.message || 'Delete failed'); }
  };

  const visible = rows.filter(r => r.category === cat);
  const active  = CATEGORIES.find(c => c.key === cat);

  return (
    <div className="space-y-4">
      {error   && <Alert type="error"   message={error} />}
      {success && <Alert type="success" message={success} />}

      <p className="text-sm text-gray-500">
        These lists fill the dropdowns across the system. Changes appear immediately — no code change needed.
      </p>

      <div className="flex gap-2 flex-wrap">
        {CATEGORIES.map(c => (
          <button key={c.key} onClick={() => setCat(c.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${cat === c.key ? 'bg-primary-600 text-white border-primary-600' : 'bg-white'}`}>
            {c.label}
            <span className="ml-1.5 text-xs opacity-70">({rows.filter(r => r.category === c.key).length})</span>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">{active?.note}</p>
        {can('admin','transport_officer') && <Button size="sm" onClick={openAdd}>+ Add to {active?.label}</Button>}
      </div>

      {loading ? <Loading /> : (
        <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
          {visible.length === 0 && <p className="px-4 py-8 text-center text-sm text-gray-400">No items yet</p>}
          {visible.map(r => (
            <div key={r.id} className={`px-4 py-2.5 flex items-center justify-between ${r.status === 'inactive' ? 'opacity-50' : ''}`}>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-300 w-6">{r.sort_order}</span>
                <span className="text-sm font-medium">{r.value}</span>
                {r.status === 'inactive' && <span className="text-xs text-gray-400">(hidden)</span>}
              </div>
              {can('admin','transport_officer') && (
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" title="Edit" onClick={() => openEdit(r)}>✏️</Button>
                  <Button variant="ghost" size="sm" title={r.status === 'active' ? 'Hide from dropdown' : 'Show in dropdown'}
                    onClick={() => toggle(r)}>{r.status === 'active' ? '🚫' : '✅'}</Button>
                  {can('admin') && <Button variant="ghost" size="sm" title="Remove" onClick={() => remove(r)}>🗑️</Button>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)}
        title={editing ? `Edit — ${editing.value}` : `Add to ${active?.label}`}>
        <form onSubmit={save} className="space-y-4">
          <Select label="List" value={form.category} disabled={!!editing}
            onChange={e => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </Select>
          <Input label="Value *" required value={form.value}
            onChange={e => setForm({ ...form, value: e.target.value })} placeholder="e.g. Double Cab" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Sort Order" type="number" value={form.sort_order}
              onChange={e => setForm({ ...form, sort_order: e.target.value })} />
            <Select label="Status" value={form.status}
              onChange={e => setForm({ ...form, status: e.target.value })}>
              <option value="active">Active</option>
              <option value="inactive">Hidden</option>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Item'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
